import { Text } from '@/components/ui/text';
import { LandingGlow } from '@/features/public-web/LandingGlow';
import { flushPostHog, trackLandingCTAClicked } from '@/lib/analytics/posthog';
import { APP_CONFIG } from '@/lib/app/config';
import { Stack, useRouter } from 'expo-router';
import * as React from 'react';
import { Image, Pressable, ScrollView, View } from 'react-native';
import Svg, { Path } from 'react-native-svg';

const WORDMARK_URL = '/static/radious-wordmark-light.svg';
const EVENTS_PREVIEW_URL = '/static/map-preview.png';

type LandingPath = '/' | '/download' | '/events';

function withLandingTracking(target: string) {
  if (typeof window === 'undefined') {
    return target;
  }

  try {
    const source = new URL(window.location.href);
    const destination = new URL(target, window.location.origin);
    for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'qr_slug']) {
      const value = source.searchParams.get(key);
      if (value && !destination.searchParams.get(key)) {
        destination.searchParams.set(key, value);
      }
    }
    return destination.toString();
  } catch {
    return target;
  }
}

function isAndroidBrowser() {
  return typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);
}

async function openExternalURL(url: string) {
  await flushPostHog();
  if (typeof window !== 'undefined') {
    window.location.assign(withLandingTracking(url));
  }
}

export default function WebHomeScreen() {
  const { push } = useRouter();
  const isAndroid = isAndroidBrowser();
  const [showStickyCTA, setShowStickyCTA] = React.useState(false);
  const stickyCTAVisibleRef = React.useRef(false);
  const androidDownloadURL = APP_CONFIG.playStoreURL || APP_CONFIG.downloadURL;
  const androidDownloadDestination = APP_CONFIG.playStoreURL ? 'google_play' : 'download_page';

  const pushLandingCTA = React.useCallback(
    (label: string, destination: string, path: LandingPath) => {
      trackLandingCTAClicked({ label, destination, href: path, pageName: 'home' });
      push(path);
    },
    [push]
  );

  const openLandingCTA = React.useCallback((label: string, destination: string, url: string) => {
    trackLandingCTAClicked({ label, destination, href: url, pageName: 'home' });
    void openExternalURL(url);
  }, []);

  const updateStickyCTAVisibility = React.useCallback((offsetY: number) => {
    const nextVisible = offsetY > 620;
    if (stickyCTAVisibleRef.current === nextVisible) {
      return;
    }
    stickyCTAVisibleRef.current = nextVisible;
    setShowStickyCTA(nextVisible);
  }, []);

  return (
    <View className="flex-1 bg-[#070a10]">
      <Stack.Screen options={{ headerShown: false, title: 'Radious' }} />
      <LandingGlow />

      <ScrollView
        className="flex-1"
        contentContainerClassName="mx-auto w-full max-w-[1080px] px-5 md:px-7"
        onScroll={(event) => updateStickyCTAVisibility(event.nativeEvent.contentOffset.y)}
        scrollEventThrottle={100}
        showsVerticalScrollIndicator={false}>
        <View className="flex-row items-center justify-between py-[26px]">
          <Pressable accessibilityRole="link" onPress={() => push('/')}>
            <Image
              accessibilityLabel="Radious home"
              className="h-[19px] w-[90px]"
              resizeMode="contain"
              source={{ uri: WORDMARK_URL }}
            />
          </Pressable>
          <Pressable
            accessibilityRole="link"
            className="rounded-md border border-white/15 px-4 py-2 active:opacity-80"
            onPress={() => pushLandingCTA('nav_open_web_app', 'web_app', '/events')}>
            <Text className="text-xs font-semibold text-[#7a6e60] no-underline">Open Web App</Text>
          </Pressable>
        </View>

        <View className="items-center gap-10 py-7 md:flex-row md:gap-16 md:py-[68px]">
          <View className="flex-1 items-center md:items-start">
            <View className="mb-[18px] flex-row items-center justify-center gap-2 md:justify-start">
              <View className="size-1.5 rounded-full bg-primary shadow-md shadow-primary" />
              <Text className="text-[11px] font-bold uppercase tracking-[2.5px] text-[#7a6e60] no-underline">
                San Antonio
              </Text>
            </View>
            <Text className="mb-[22px] text-center text-[44px] font-extrabold uppercase leading-none tracking-[-0.5px] text-[#f5eddf] no-underline md:text-left md:text-[72px]">
              SA has plans{'\n'}tonight.{'\n'}
              <Text className="text-[44px] font-extrabold uppercase leading-none tracking-[-0.5px] text-primary no-underline md:text-[72px]">
                Do you?
              </Text>
            </Text>
            <Text className="mb-8 max-w-[400px] text-center text-[15px] leading-[26px] text-[#c4b69e] no-underline md:text-left">
              Every bar, party, pop-up, and last-minute plan in San Antonio, in one place. No more
              {"scrolling three apps and a group chat that's already moved on."}
            </Text>

            {isAndroid ? (
              <>
                <View className="w-full max-w-[320px] border-t border-white/10 pt-5">
                  <View className="mb-2.5 flex-row items-center self-start rounded-full border border-white/15 bg-white/5 px-3 py-1.5">
                    <View className="mr-1.5 size-1.5 rounded-full bg-[#a8d8a8]" />
                    <Text className="text-[11px] font-bold uppercase tracking-[1.5px] text-[#c4b69e] no-underline">
                      Android
                    </Text>
                  </View>
                  <Text className="mb-2.5 text-xs leading-[19px] text-[#7a6e60] no-underline">
                    Open Radious on the web now, or check the download page for Android availability.
                  </Text>
                  <View className="gap-2">
                    <Pressable
                      accessibilityRole="link"
                      className="min-h-10 items-center justify-center rounded-md border border-white/15 px-[18px] py-2.5 active:opacity-80"
                      onPress={() =>
                        openLandingCTA(
                          'android_download',
                          androidDownloadDestination,
                          androidDownloadURL
                        )
                      }>
                      <Text className="text-[13px] font-semibold text-[#c4b69e] no-underline">
                        {APP_CONFIG.playStoreURL ? 'Open Google Play' : 'Open downloads'}
                      </Text>
                    </Pressable>
                    <Pressable
                      accessibilityRole="link"
                      className="min-h-10 items-center justify-center rounded-md bg-primary px-[18px] py-2.5 active:opacity-80"
                      onPress={() => pushLandingCTA('android_open_web_app', 'web_app', '/events')}>
                      <Text className="text-[13px] font-semibold text-white no-underline">
                        Open Web App
                      </Text>
                    </Pressable>
                  </View>
                </View>
              </>
            ) : (
              <>
                <View className="mb-5 flex-row flex-wrap items-center justify-center gap-3.5 md:justify-start">
                  <View className="gap-1.5">
                    <Pressable
                      accessibilityRole="link"
                      className="w-full max-w-[320px] flex-row items-center justify-center gap-2 rounded-[7px] bg-primary px-[30px] py-[15px] shadow-xl shadow-primary/20 active:opacity-80 md:w-auto"
                      onPress={() =>
                        pushLandingCTA('hero_download_app_store', 'download_page', '/download')
                      }>
                      <AppleIcon color="#ffffff" />
                      <Text className="text-sm font-bold text-white no-underline">
                        Download on the App Store
                      </Text>
                    </Pressable>
                    <Text
                      className="text-center text-[12px] no-underline"
                      style={{ color: 'rgba(255,255,255,0.35)' }}>
                      Free on the App Store for iPhone.
                    </Text>
                  </View>
                  <Pressable
                    accessibilityRole="link"
                    className="active:opacity-75"
                    style={{ marginLeft: 16 }}
                    onPress={() => pushLandingCTA('hero_open_web_app', 'web_app', '/events')}>
                    <Text
                      className="text-[15px] font-semibold no-underline"
                      style={{ color: 'rgba(255,255,255,0.65)' }}>
                      Open Web App →
                    </Text>
                  </Pressable>
                </View>
                <View
                  className="w-full max-w-[320px] rounded-[12px] px-5 py-4"
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.04)',
                    borderColor: 'rgba(255,255,255,0.08)',
                    borderWidth: 1,
                  }}>
                  <Text
                    className="mb-3 text-[13px] no-underline"
                    style={{ color: 'rgba(255,255,255,0.45)' }}>
                    {"On Android? Open the web app now or check the download page."}
                  </Text>
                  <View className="gap-2 md:flex-row">
                    <Pressable
                      accessibilityRole="link"
                      className="min-h-10 flex-1 items-center justify-center rounded-md border border-white/15 px-[18px] py-2.5 active:opacity-80"
                      onPress={() => pushLandingCTA('android_card_open_web_app', 'web_app', '/events')}>
                      <Text className="text-[13px] font-semibold text-[#c4b69e] no-underline">
                        Open Web App
                      </Text>
                    </Pressable>
                    <Pressable
                      accessibilityRole="link"
                      className="min-h-10 flex-1 items-center justify-center rounded-md border border-white/15 px-[18px] py-2.5 active:opacity-80"
                      onPress={() =>
                        openLandingCTA(
                          'android_card_download',
                          androidDownloadDestination,
                          androidDownloadURL
                        )
                      }>
                      <Text className="text-[13px] font-semibold text-[#c4b69e] no-underline">
                        Downloads
                      </Text>
                    </Pressable>
                  </View>
                </View>
              </>
            )}
          </View>

          <View className="w-full items-center md:w-[390px]" style={{ filter: 'drop-shadow(0 24px 60px rgba(0,0,0,0.55))' } as any}>
            <View className="h-[462px] w-[230px] overflow-hidden rounded-[36px] border-[5px] border-white/10 bg-[#080808] md:h-[663px] md:w-[330px] md:rounded-[42px]">
              <Image
                accessibilityLabel="Radious map preview — live events around San Antonio"
                className="h-full w-full"
                resizeMode="cover"
                source={{ uri: EVENTS_PREVIEW_URL }}
              />
            </View>
          </View>
        </View>

        <View className="mb-[88px] border-y border-white/10 pt-12 md:flex-row">
          <Pillar
            eyebrow="Built local"
            title="Made for San Antonio"
            body="Every event hand-checked for SA. No scraped garbage, no sold-out shows from two weeks ago."
          />
          <Pillar
            eyebrow="Social"
            title="See who's going"
            body="Know who's already in before you decide. Then loop in your crew."
            middle
          />
          <Pillar
            eyebrow="Right now"
            title="Tonight, not next month"
            body="Filter by what's happening right now. Bars, pop-ups, live music — spontaneous is fine."
            last
          />
        </View>

        <View className="pb-[72px] md:pb-[88px]">
          <Text className="mb-2.5 text-[10px] font-bold uppercase tracking-[2.5px] text-primary no-underline">
            Why Radious
          </Text>
          <Text className="mb-12 text-[26px] font-extrabold uppercase leading-none text-[#f5eddf] no-underline md:text-[40px]">
            The group chat{'\n'}has failed you enough.
          </Text>
          <WhyRow
            number="01"
            title="Everything in one place"
            body="Stop checking the bar's Instagram, then Eventbrite, then a group chat from three weeks ago. One place. Updated today."
          />
          <WhyRow
            number="02"
            title="Locals curate it"
            body="We're not scraping the internet. Every event gets reviewed. You get the actual underground shows and rooftop pop-ups — not the stuff that sold out before you heard about it."
          />
          <WhyRow
            number="03"
            title="Free. Always."
            body="No subscriptions, no paywalled events. Free to use so there's no reason your crew isn't already on it."
            last
          />
        </View>

        <View className="items-center pb-[72px] md:items-end md:pb-[88px]">
          <View className="w-full max-w-[640px]">
            <Text className="mb-2.5 text-[10px] font-bold uppercase tracking-[2.5px] text-primary no-underline">
              Inside the app
            </Text>
            <Text className="mb-[18px] text-[38px] font-extrabold uppercase leading-none text-[#f5eddf] no-underline md:text-[44px]">
              {"Everything you need.\nNothing you don't."}
            </Text>
            <Text className="mb-7 max-w-[520px] text-[15px] leading-[26px] text-[#c4b69e] no-underline">
              {"Tap an event and you've got everything — the vibe, the venue, what to expect, and who's already planning to show up. No links to click, no browser tabs."}
            </Text>
            <View className="gap-[13px] border-t border-white/10 pt-6">
              <DetailItem label="Mark yourself as going — your crew sees it instantly" />
              <DetailItem label="Venue info, hours, and map built right in" />
              <DetailItem label="Save events for later, come back anytime" />
              <DetailItem label="Share without leaving the app" />
            </View>
          </View>
        </View>

        <View className="gap-4 border-t border-white/10 pb-[72px] pt-8 md:flex-row md:items-center md:gap-5 md:pb-24 md:pt-10">
          <Text className="min-w-[200px] flex-1 text-xl font-bold uppercase text-[#f5eddf] no-underline">
            {"That's the whole pitch. →"}
          </Text>
          <Pressable
            accessibilityRole="link"
            className="w-full max-w-[320px] flex-row items-center justify-center gap-2 rounded-[7px] bg-primary px-[30px] py-[15px] shadow-xl shadow-primary/20 active:opacity-80"
            onPress={() => pushLandingCTA('bottom_get_app', 'download_page', '/download')}>
            <AppleIcon color="#ffffff" />
            <Text className="text-sm font-bold text-white no-underline">Get the app</Text>
          </Pressable>
        </View>

        <View className="pb-[72px] md:pb-20">
          <Text className="mb-2.5 text-[10px] font-bold uppercase tracking-[2.5px] text-primary no-underline">
            Questions
          </Text>
          <Text className="text-[26px] font-extrabold uppercase leading-none text-[#f5eddf] no-underline md:text-[40px]">
            Quick answers.
          </Text>
          <View className="mt-9 max-w-[620px]">
            <FAQItem
              question="Is the app out yet?"
              answer="Yes. We're live on the App Store, and there's a web app at radious.app. Android is coming soon."
            />
            <FAQItem
              question="Is it free?"
              answer="Yep. Free to download, free to use. No tiers, no paywalled events."
            />
            <FAQItem
              question="Why San Antonio first?"
              answer="Because it's home. SA was always going to be first."
            />
            <FAQItem
              question="How do I list my event or venue?"
              answer="Download the app and post your event in minutes. Or hit us at post@radious.app or @radious.app on Instagram and we'll help you get set up. Venue owners can claim their spot right in the app too."
            />
            <FAQItem
              question="What about my privacy?"
              answer="We don't sell your data. Full stop. Privacy Policy and Terms of Service are always there if you want the fine print."
            />
            <FAQItem
              question="How do I get in touch?"
              answer="Email hello@radious.app or DM @radious.app on Instagram — we actually respond."
              last
            />
          </View>
        </View>

        <View className="gap-3 border-t border-white/10 pb-[100px] pt-[26px] md:flex-row md:items-center md:justify-between md:pb-10">
          <Text className="text-center text-xs text-[#7a6e60] no-underline">© 2026 Radious</Text>
          <View className="flex-row flex-wrap justify-center gap-5">
            <FooterLink label="Privacy" onPress={() => push('/privacy')} />
            <FooterLink label="Terms" onPress={() => push('/terms-of-service')} />
            <FooterLink
              label="Status"
              onPress={() =>
                openLandingCTA(
                  'footer_status',
                  'status_page',
                  'https://stats.uptimerobot.com/CFnDDHkwDJ'
                )
              }
            />
            <FooterLink
              label="Contact"
              onPress={() => openLandingCTA('footer_contact', 'email', 'mailto:hello@radious.app')}
            />
            <FooterLink
              label="Instagram"
              onPress={() =>
                openLandingCTA(
                  'footer_instagram',
                  'instagram',
                  'https://instagram.com/radious.app'
                )
              }
            />
            <FooterLink
              label="X"
              onPress={() => openLandingCTA('footer_x', 'x', 'https://x.com/getradious')}
            />
          </View>
        </View>
      </ScrollView>

      {showStickyCTA ? (
        <View className="absolute bottom-0 left-0 right-0 bg-[#070a10] px-5 pb-6 pt-3 md:hidden">
          <Pressable
            accessibilityRole="link"
            className="w-full flex-row items-center justify-center gap-2 rounded-[7px] bg-primary px-6 py-3.5 shadow-xl shadow-primary/30 active:opacity-80"
            onPress={() =>
              isAndroid
                ? pushLandingCTA('sticky_android_open_web_app', 'web_app', '/events')
                : pushLandingCTA('sticky_get_app', 'download_page', '/download')
            }>
            {!isAndroid ? <AppleIcon color="#ffffff" /> : null}
            <Text className="text-sm font-bold text-white no-underline">
              {isAndroid ? 'Open Web App' : 'Get the app'}
            </Text>
          </Pressable>
          <Pressable
            accessibilityRole="link"
            onPress={() => pushLandingCTA('sticky_open_web_app', 'web_app', '/events')}>
            <Text className="mt-2.5 text-center text-xs font-semibold text-[#c4b69e] no-underline">
              {isAndroid ? 'Or open the web app now.' : 'Not on iPhone? Open the web app.'}
            </Text>
          </Pressable>
        </View>
      ) : null}
    </View>
  );
}

function Pillar({
  eyebrow,
  title,
  body,
  middle = false,
  last = false,
}: {
  eyebrow: string;
  title: string;
  body: string;
  middle?: boolean;
  last?: boolean;
}) {
  return (
    <View
      className={[
        'flex-1 border-b border-white/10 py-6 md:border-b-0 md:border-r md:py-[30px] md:pr-9',
        middle ? 'md:pl-9' : '',
        last ? 'border-r-0 md:pl-9 md:pr-0' : '',
      ].join(' ')}>
      <Text className="mb-2 text-[10px] font-bold uppercase tracking-[2px] text-primary no-underline">
        {eyebrow}
      </Text>
      <Text className="mb-2 text-base font-bold uppercase text-[#f5eddf] no-underline">
        {title}
      </Text>
      <Text className="text-[13px] leading-[21px] text-[#c4b69e] no-underline">{body}</Text>
    </View>
  );
}

function WhyRow({
  number,
  title,
  body,
  last = false,
}: {
  number: string;
  title: string;
  body: string;
  last?: boolean;
}) {
  return (
    <View
      className={[
        'flex-row gap-5 border-t border-white/10 py-9 md:gap-9',
        last ? 'border-b border-white/10' : '',
      ].join(' ')}>
      <Text className="w-20 shrink-0 whitespace-nowrap text-left text-4xl font-extrabold leading-none tracking-[-2px] text-primary/30 no-underline md:w-24 md:text-5xl">
        {number}
      </Text>
      <View className="flex-1">
        <Text className="mb-2 text-xl font-bold uppercase text-[#f5eddf] no-underline">
          {title}
        </Text>
        <Text className="max-w-[560px] text-sm leading-[25px] text-[#c4b69e] no-underline">
          {body}
        </Text>
      </View>
    </View>
  );
}

function DetailItem({ label }: { label: string }) {
  return (
    <View className="flex-row items-start gap-[11px]">
      <View className="mt-1.5 size-[7px] rounded-full bg-primary" />
      <Text className="flex-1 text-sm leading-[21px] text-[#c4b69e] no-underline">{label}</Text>
    </View>
  );
}

function FAQItem({
  question,
  answer,
  last = false,
}: {
  question: string;
  answer: string;
  last?: boolean;
}) {
  return (
    <View className={['border-t border-white/10 py-[22px]', last ? 'border-b' : ''].join(' ')}>
      <Text className="mb-[7px] text-sm font-bold text-[#f5eddf] no-underline">{question}</Text>
      <Text className="text-sm leading-6 text-[#c4b69e] no-underline">{answer}</Text>
    </View>
  );
}

function FooterLink({ label, onPress }: { label: string; onPress: () => void }) {
  return (
    <Pressable accessibilityRole="link" onPress={onPress}>
      <Text className="text-xs text-[#7a6e60] no-underline">{label}</Text>
    </Pressable>
  );
}

function AppleIcon({ color }: { color: string }) {
  return (
    <Svg width={16} height={16} viewBox="0 0 24 24" aria-hidden>
      <Path
        fill={color}
        d="M16.365 1.43c.038 1.01-.295 1.998-.915 2.856-.779 1.076-2.053 1.905-3.325 1.799-.162-1.008.295-2.083.903-2.827.754-.938 2.075-1.646 3.337-1.828ZM20.87 17.14c-.41.93-.9 1.79-1.507 2.585-.83 1.087-1.505 2.438-2.86 2.45-1.204.012-1.59-.778-2.967-.771-1.378.006-1.789.783-2.99.759-1.31-.02-2.032-1.237-2.868-2.321-2.338-3.032-2.584-6.588-1.143-8.79 1.023-1.57 2.64-2.493 4.16-2.493 1.225 0 2.38.824 3.183.824.804 0 2.311-.998 3.894-.852.662.027 2.521.267 3.714 1.975-.096.06-2.217 1.272-2.193 3.795.026 3.015 2.636 4.019 2.677 4.039-.022.067-.413 1.406-1.1 2.8Z"
      />
    </Svg>
  );
}
